'use client'

import type { CSSVariablesResolver, MantineTheme } from '@mantine/core'
import { rebrandCssResolver } from '@nile/mantine-theme/theme/rebrand'
import { WALMART_HEADING_SCALE } from '@/tenants/walmart/theme/typography'
import { WALMART_BRAND } from '@/config/tenants/walmartPublic'
import { walmartEverydaySans } from '@/tenants/walmart/fonts/everydaySans'
import { WALMART_INK } from '@/tenants/walmart/theme/walmartTheme'
import { headingScaleVariables } from '@/config/tenants/tokens'
import { WALMART_FLOW_MAX_WIDTH } from '@/tenants/walmart/theme/layout'

const WALMART_FONT_FAMILY = `${walmartEverydaySans.style.fontFamily}, var(--font-plus-jakarta-sans), sans-serif`

const WALMART_GRAY = {
    lighter: '#ffffff',
    0: '#f8f8f8',
    1: '#f1f1f2',
    2: '#e3e4e5',
    3: '#babbbe',
    4: '#94969b',
    5: '#74767c',
    6: '#515357',
    7: '#46474a',
    8: '#3a3b3e',
    9: '#2e2f32',
} as const

export const walmartCssResolver: CSSVariablesResolver = (theme: MantineTheme) => {
    const base = rebrandCssResolver(theme)

    return {
        variables: {
            ...base.variables,

            '--gm-gray-lighter': WALMART_GRAY.lighter,
            '--gm-gray-0': WALMART_GRAY[0],
            '--gm-gray-1': WALMART_GRAY[1],
            '--gm-gray-2': WALMART_GRAY[2],
            '--gm-gray-3': WALMART_GRAY[3],
            '--gm-gray-4': WALMART_GRAY[4],
            '--gm-gray-5': WALMART_GRAY[5],
            '--gm-gray-6': WALMART_GRAY[6],
            '--gm-gray-7': WALMART_GRAY[7],
            '--gm-gray-8': WALMART_GRAY[8],
            '--gm-gray-9': WALMART_GRAY[9],

            '--gm-blue-0': WALMART_INK[0],
            '--gm-blue-1': WALMART_INK[1],
            '--gm-blue-2': WALMART_INK[2],
            '--gm-blue-3': WALMART_INK[3],
            '--gm-blue-4': WALMART_INK[4],
            '--gm-blue-5': WALMART_INK[5],
            '--gm-blue-6': WALMART_INK[6],
            '--gm-blue-7': WALMART_INK[7],
            '--gm-blue-8': WALMART_INK[8],
            '--gm-blue-9': WALMART_INK[9],

            '--gm-bg-solid': WALMART_GRAY.lighter,
            '--gm-bg-primary': WALMART_GRAY.lighter,

            '--walmart-surface-accent-teal': '#e1f3f8',
            '--walmart-text-on-accent-teal': '#005a6f',
            '--walmart-surface-accent-green': '#eaf3e6',
            '--walmart-text-on-accent-green': '#1d5f02',
            '--walmart-border-card': '#cdcdcd',
            '--walmart-separator': '#e3e4e5',
            '--walmart-text-accent-green': '#2a8703',
            '--walmart-text-accent-blue': '#0053e2',
            '--gm-persona-frame-width': '100%',
            '--gm-persona-frame-max-width': `${WALMART_FLOW_MAX_WIDTH}px`,
            '--gm-care-plan-page-padding-inline': '0px',
            '--gm-care-plan-section-gap': '32px',
            '--gm-care-plan-title-font-size': '1.75rem',
            '--gm-care-plan-title-font-weight': '700',
            '--gm-care-plan-diagnosis-font-size': '1.5rem',
            '--gm-care-plan-diagnosis-font-weight': '400',
            '--gm-care-section-title-font-size': '1.5rem',
            '--gm-care-section-title-font-weight': '400',
            '--gm-care-item-title-font-weight': '400',
            '--gm-care-prescription-title-font-size': '1.125rem',
            '--gm-care-prescription-title-font-weight': '400',
            '--gm-care-curation-label-color': 'var(--walmart-text-accent-blue)',
            '--walmart-text-promotion': '#488424',
            '--walmart-surface-brand-subtle': '#e9f1fe',
            '--walmart-text-on-brand-subtle': '#114ab6',
            '--walmart-surface-accent-red': '#fde9e8',
            '--walmart-text-on-accent-red': '#a20c00',
            '--walmart-surface-accent-orange': '#fff0e6',
            '--walmart-text-on-accent-orange': '#af2f00',
            '--walmart-text-unread': '#1762e5',
            '--walmart-payer-bcbs-blue': '#0098d8',
            '--walmart-field-border': '#909196',
            '--walmart-elevation-100': '0 1px 2px 1px rgba(0, 0, 0, 0.15), 0 -1px 2px 0 rgba(0, 0, 0, 0.1)',
            '--walmart-action-fill-hover': WALMART_BRAND[6],
            '--walmart-action-border-hover': '#000000',
            '--walmart-action-surface-hover': WALMART_GRAY[1],
            '--walmart-action-fill-strong': '#070821',
            '--walmart-action-text-on-strong': '#f7f1ee',
            '--walmart-surface-dialog': '#f7f1ee',
            '--walmart-surface-subtle': '#f7f7f7',

            '--gm-text-xs-line-height': '16px',
            '--gm-text-s-line-height': '20px',
            '--gm-text-m-line-height': '24px',
            '--gm-text-l-line-height': '24px',
            '--gm-text-xl-line-height': '28px',
            '--gm-text-2xl-line-height': '32px',
            '--gm-display-s-font-size': '28px',
            '--gm-display-s-line-height': '36px',
            '--gm-display-s-letter-spacing': '0',

            ...headingScaleVariables(WALMART_HEADING_SCALE),

            '--gm-heading-h1-font-weight': '700',
            '--gm-heading-h2-font-weight': '700',
            '--gm-heading-h3-font-weight': '700',
            '--gm-heading-h4-font-weight': '700',

            '--gm-background-bg-action': 'var(--walmart-action-fill-strong)',
            '--gm-action-height': '40px',
            '--gm-action-md-height': '40px',
            '--gm-switch-to-chat-display': 'none',
            '--gm-sticky-footer-shadow': 'none',
            '--gm-sticky-footer-padding-top': '16px',
            '--gm-sticky-footer-gap': '8px',
            '--gm-sticky-footer-summary-font-size': '14px',
            '--gm-sticky-footer-summary-line-height': '20px',
            '--gm-sticky-footer-summary-weight': '400',
            '--gm-sticky-footer-padding-bottom': 'calc(16px + env(safe-area-inset-bottom, 0px))',

            '--gm-illustration-container-bg': 'var(--gm-gray-1)',
            '--gm-illustration-container-border': 'transparent',
            '--gm-alert-spot-bg': '#fff9e9',
            '--gm-alert-spot-size': '59.2px',
            '--gm-alert-spot-icon-size': '32px',
            '--gm-alert-spot-icon-color': '#995213',
            '--gm-alert-spot-gap': '24px',
            '--gm-alert-spot-text-gap': '8px',
            '--gm-alert-spot-body-color': 'var(--gm-foreground-fg-secondary)',
            '--gm-interstitial-body-font-size': '18px',
            '--gm-interstitial-body-line-height': '24px',
            '--gm-interstitial-body-color': 'var(--gm-foreground-fg-secondary)',
            '--gm-inline-link-color': 'var(--gm-foreground-fg-primary)',
            '--gm-inline-link-font-size': '14px',
            '--gm-inline-link-font-weight': '400',
            '--gm-flow-body-emphasis-weight': '400',
            '--gm-flow-subtitle-font-size': '16px',
            '--gm-flow-subtitle-line-height': '24px',
            '--gm-flow-subtitle-color': 'var(--gm-foreground-fg-primary)',
            '--gm-flow-section-gap': '16px',
            '--gm-scheduler-day-height': '58px',
            '--gm-scheduler-day-min-width': '80px',
            '--gm-scheduler-day-padding-inline': '8px',
            '--gm-scheduler-day-padding-block': '7px',
            '--gm-scheduler-day-border-color': 'var(--gm-gray-5)',
            '--gm-scheduler-day-radius': '8px',
            '--gm-scheduler-day-align': 'center',
            '--gm-scheduler-day-gap': '8px',
            '--gm-scheduler-day-shadow': 'inset 0 0 0 1px var(--gm-gray-9)',
            '--gm-scheduler-day-weekday-font-size': '14px',
            '--gm-scheduler-day-date-color': 'var(--gm-foreground-fg-primary)',
            '--gm-scheduler-label-weight': '400',
            '--gm-scheduler-header-gap': '16px',
            '--gm-scheduler-slot-gap': '16px',
            '--gm-scheduler-slot-padding-inline': '16px',
            '--gm-scheduler-slot-padding-block': '15px',
            '--gm-scheduler-slot-label-font-size': '16px',
            '--gm-scheduler-slot-label-line-height': '24px',
            '--gm-scheduler-slot-radio-gap': '12px',
            '--gm-account-content-inset': '16px',
            '--gm-account-shell-offset': '0px',
            '--gm-search-pill-border': '1px solid var(--gm-gray-5)',
            '--gm-search-pill-shadow': 'none',
            '--gm-pharmacy-card-title-weight': '400',
            '--gm-pharmacy-rating-gap': '8px',
            '--gm-pharmacy-rating-star-color': '#ffc220',
            '--gm-secondary-action-border': 'var(--gm-foreground-fg-primary)',
            '--gm-completion-spot-bg': 'var(--walmart-surface-accent-green)',
            '--gm-completion-spot-color': 'var(--walmart-text-accent-green)',
            '--gm-next-steps-message-font-size': '18px',
            '--gm-payment-card-bg': 'var(--gm-bg-solid)',
            '--gm-payment-card-border': '1px solid var(--walmart-separator)',
            '--gm-payment-card-shadow': 'none',
            '--gm-account-page-gap': '16px',
            '--gm-account-empty-state-justify': 'flex-start',
            '--gm-account-empty-state-min-height': '0px',
            '--gm-account-empty-state-padding-top': '48px',
            '--gm-account-empty-state-page-gap': '24px',
            '--gm-account-page-title-font-size': '1.75rem',
            '--gm-account-page-title-font-weight': '700',
            '--gm-account-page-title-line-height': '2.5rem',
            '--gm-account-page-title-padding-block': '8px',
            '--gm-account-page-title-letter-spacing': '0',
            '--gm-text-link-decoration': 'underline',
            '--gm-account-section-title-font-size': '24px',
            '--gm-account-section-title-font-weight': '400',
            '--gm-account-section-title-line-height': '32px',
            '--gm-account-section-title-gap': '24px',
            '--gm-account-section-body-font-size': '18px',
            '--gm-account-section-body-line-height': '1.3',
            '--gm-lab-result-gap': '8px',
            '--gm-lab-result-name-font-size': '16px',
            '--gm-lab-result-value-font-size': '26px',
            '--gm-lab-result-value-font-weight': '400',
            '--gm-lab-result-range-font-size': '16px',
            '--gm-lab-result-range-font-weight': '400',
            '--gm-lab-result-range-line-height': '1.4',
            '--gm-lab-result-body-font-size': '16px',
            '--gm-lab-result-body-line-height': '1.4',
            '--gm-lab-result-body-color': 'var(--gm-foreground-fg-secondary)',
            '--gm-review-intro-gap': '0px',
            '--gm-review-headline-padding-block': '8px',
            '--gm-review-row-padding-block': '20px',
            '--gm-review-row-color': 'var(--gm-foreground-fg-primary)',
            '--gm-review-row-weight': '400',
            '--gm-review-row-line-height': '24px',
            '--gm-search-row-padding-block': '12px',
            '--gm-search-row-font-size': '16px',
            '--gm-confirmation-action-height': '40px',
            '--gm-interstitial-body-font-weight': '400',
            '--gm-action-bar-button-width': '100%',
            '--gm-flow-title-gap': '8px',
            '--gm-search-actions-gap': '8px',
            '--gm-search-actions-offset': '0px',
            '--gm-search-result-icon-display': 'flex',
            '--gm-carrier-grid-offset': '0px',
            '--gm-carrier-grid-gap': '8px',
            '--gm-carrier-tile-radius': '8px',
            '--gm-carrier-tile-shadow': 'none',
            '--gm-file-upload-tile-radius': '8px',
            '--gm-file-upload-tile-shadow': 'none',
            '--gm-file-upload-row-radius': '8px',
            '--gm-file-upload-row-shadow': 'none',
            '--gm-file-upload-preview-bg': 'var(--gm-gray-0)',
            '--gm-file-upload-remove-color': 'var(--gm-gray-9)',
            '--gm-file-upload-remove-hover': 'var(--gm-gray-0)',
            '--gm-selection-fill': 'var(--walmart-action-fill-strong)',
            '--gm-selection-fill-hover': 'var(--walmart-action-fill-hover)',
            '--gm-camera-frame-width': 'calc(100vw - 32px)',
            '--gm-camera-frame-color': 'var(--gm-blue-6)',
            '--gm-camera-frame-label-padding-block': '8px',
            '--gm-camera-frame-label-color': '#ffffff',
            '--gm-camera-frame-label-font-size': '16px',
            '--gm-camera-frame-label-line-height': '24px',
            '--gm-camera-label-weight': '700',
            '--gm-camera-hint-line-height': '24px',
            '--gm-data-label-font-size': '14px',
            '--gm-data-label-font-weight': '400',
            '--gm-data-value-font-size': '16px',
            '--gm-flow-body-emphasis-line-height': '24px',
            '--gm-action-radius': '100px',
            '--gm-action-lg-height': '40px',
            '--gm-action-lg-font-size': '16px',
            '--gm-action-lg-line-height': '24px',
            '--gm-action-font-weight': '700',
            '--gm-action-label-weight': '700',
            '--gm-text-input-height': '56px',
            '--gm-date-input-height': '56px',
            '--gm-code-input-width': '42px',
            '--gm-code-input-height': '52px',
            '--gm-code-input-min-height': '52px',
            '--gm-code-input-radius': '8px',
            '--gm-code-input-font-size': '24px',
            '--gm-code-input-font-weight': '700',
            '--gm-flow-content-inset': '0px',
            '--gm-field-radius': '4px',
            '--gm-field-font-size': '16px',
            '--gm-select-input-padding': '0 16px',
            '--gm-select-height': 'var(--gm-text-input-height)',
            '--gm-select-radius': 'var(--gm-field-radius)',
            '--gm-checkbox-radius': '2px',
            '--gm-field-leading-icon-display': 'none',
            '--gm-field-leading-icon-inset': '16px',
            '--gm-field-border-color': '#909196',
            '--gm-field-bg': WALMART_GRAY.lighter,
            '--gm-field-shadow': 'none',

            '--gm-field-label-weight': '700',
            '--gm-foreground-fg-placeholder': WALMART_GRAY[6],
            '--gm-text-legal-font-size': '12px',
            '--gm-text-legal-line-height': '16px',
            '--gm-foreground-fg-accent': 'var(--gm-foreground-fg-primary)',

            '--gm-font-family': WALMART_FONT_FAMILY,

            '--mantine-radius-lg': 'var(--gm-radius-8)',
        },
        light: {
            ...base.light,
            '--mantine-color-body': 'var(--gm-bg-solid)',
            '--mantine-color-blue-filled-hover': 'var(--walmart-action-fill-hover)',
            '--mantine-color-walmartInk-filled-hover': 'var(--walmart-action-fill-hover)',
        },
        dark: {
            ...base.dark,
            '--mantine-color-body': 'var(--gm-bg-solid)',
            '--mantine-color-blue-filled-hover': 'var(--walmart-action-fill-hover)',
            '--mantine-color-walmartInk-filled-hover': 'var(--walmart-action-fill-hover)',
        },
    }
}
